import { use } from 'react';

import styled from '@emotion/styled';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';
import {
  needsManualTabSelection,
  SurveyThemeContext,
} from '@/shared-utilities';

import ProjectUsabilityCameraSetup from './project-usability-camera-setup';

interface Props {
  projectId: string;
  startRecording: () => void;
  isLoading?: boolean;
  showCameraSetup?: boolean;
}

const ThemedRoot = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

const renderEmphasis = (chunks: React.ReactNode) => <strong>{chunks}</strong>;

const ProjectUsabilityResponseWelcomeModal = ({
  projectId,
  startRecording,
  isLoading = false,
  showCameraSetup = true,
}: Props) => {
  const t = useTranslations('UserTest');

  const { styles } = use(SurveyThemeContext);

  const showTabSelectionHint = needsManualTabSelection();

  return (
    <ThemedRoot
      themeStyles={styles}
      data-test='usability-welcome-modal'
      className='flex min-h-dvh items-center justify-center bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat p-6'
    >
      <div className='mx-4 flex max-w-md flex-col items-center gap-6 rounded-lg bg-white p-8 shadow-lg'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <h2 className='text-center text-2xl font-semibold text-gray-900'>
              {t('preSession.welcomeTitle')}
            </h2>
            <p className='text-sm text-gray-600'>
              {t('preSession.welcomeBody')}
            </p>
          </div>
          {/* Important instruction - only show for Firefox, Safari, etc. */}
          {showTabSelectionHint && (
            <div className='flex gap-3 rounded-md bg-blue-50 p-4'>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className='mt-0.5 size-5 shrink-0 text-blue-500'
              />
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium text-blue-900'>
                  {t('preSession.tabHintTitle')}
                </p>
                <p className='text-xs text-blue-700'>
                  {t.rich('preSession.tabHintBody', {
                    emphasis: renderEmphasis,
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {showCameraSetup && (
          <ProjectUsabilityCameraSetup projectId={projectId} />
        )}

        <Button
          onClick={startRecording}
          variant='primary'
          size='lg'
          className='w-full'
          disabled={isLoading}
          data-test='usability-welcome-continue-button'
        >
          {isLoading
            ? t('preSession.waitingForPermissions')
            : t('preSession.continue')}
        </Button>
      </div>
    </ThemedRoot>
  );
};

export default ProjectUsabilityResponseWelcomeModal;
